import { ReactNode } from 'react'
import { useHideProperties } from 'hooks/useHideProperties'
import { useAssignedProperties } from 'hooks/useAssignedProperties'
import { Assignee } from 'api/clients/PropertyClient'
import { useProperties as usePropertiesQuery } from 'hooks/queries/companies/properties/useProperties'
import { EditorView } from 'prosemirror-view'

type UsePropertiesParameters = Parameters<typeof usePropertiesQuery>

interface Props {
  assignee: Assignee
  propertyId?: number
  view?: EditorView
  propertiesQueryArgs?: {
    queryParams?: UsePropertiesParameters[0]
    reactQueryOptions?: UsePropertiesParameters[1]
    options?: UsePropertiesParameters[2]
  }
  children: (
    assignedProperties: ReturnType<typeof useAssignedProperties>,
    propertiesQueryResult?: ReturnType<typeof usePropertiesQuery>,
  ) => ReactNode
}

const PropertiesRenderer = ({ assignee, propertyId, propertiesQueryArgs, view, children }: Props) => {
  const propertiesQueryResult = usePropertiesQuery(
    propertiesQueryArgs?.queryParams ?? {},
    propertiesQueryArgs?.reactQueryOptions ?? {},
    propertiesQueryArgs?.options ?? {},
  )
  const assignedProperties = useAssignedProperties({ assignee, propertyId, view })

  return <>{children(assignedProperties, propertiesQueryResult)}</>
}

/**
 * Wrapper component that conditionally renders useAssignedProperties hook
 * only when hideProperties is false. This allows us to avoid calling the hook
 * when properties are hidden.
 */
export const ConditionalAssignedProperties = ({ assignee, propertyId, propertiesQueryArgs, view, children }: Props) => {
  const hideProperties = useHideProperties()

  if (hideProperties) {
    return null
  }

  return (
    <PropertiesRenderer
      assignee={assignee}
      propertyId={propertyId}
      propertiesQueryArgs={propertiesQueryArgs}
      view={view}
    >
      {children}
    </PropertiesRenderer>
  )
}
